import app from 'flarum/forum/app';
import m from 'mithril';
import GuestNoticeModal from '../components/GuestNoticeModal';
import { showGuestNoticeLayer } from '../components/GuestNoticeLayer';
import { markLandingSeen } from './landingState';

/*
 * 游客「只读列表、不能看正文」的工具集。
 *
 * 需求：未注册（未登录）用户可以正常浏览论坛列表/板块，
 * 但点进帖子时不显示内容，而是提示「请尽快找任课老师绑定班级」。
 *
 * 后端同源逻辑在 src/Http/Middleware/RestrictGuestContent.php —— 两边配合，
 * 前端负责体验（点不动、直接给提示），后端负责安全（手输 API 也拿不到正文）。
 */

export const NOTICE = '请尽快找任课老师绑定班级';

/** 已经绑定过班级、但看的是别的班的内容时用这句 */
export const NOTICE_BOUND = '你没有查看该班级内容的权限';

/*
 * 当前该用哪句提示。
 *
 * 后端把 isBound / myClasses 写进了 payload 的 acmeWechat（见
 * Http\Middleware\HideRestrictedContent::withDiagnostics），前端据此区分：
 *   isBound = true  → 已绑定某班，看不了说明是「别的班」的内容
 *   isBound = false → 游客 / 还没绑定班级
 */
export function currentNotice() {
  try {
    const cfg = app.forum && app.forum.attribute ? app.forum.attribute('acmeWechat') : null;

    if (cfg && cfg.isBound) return NOTICE_BOUND;
  } catch (e) {
    // forum 还没加载完时按「未绑定」处理
  }

  return NOTICE;
}

/*
 * 是否未登录。
 *
 * 【踩坑】本组件是在 extension initializer 里 m.mount 的，那一刻
 * app.session 可能还是 undefined —— 必须防御式读取，否则整个扩展
 * "failed to initialize"。
 */
export function isGuest() {
  /*
   * 【重要】app.session 尚未就绪（undefined）时按「已登录」处理。
   *
   * 老写法 `!(app.session && app.session.user)` 在 session 还没建立时
   * 会返回 true（= 当作游客），于是正文被隐藏、点击被拦截 ——
   * 部分页面/时序下已登录用户就会「只看得到标题、看不到内容」。
   * 这里宁可放过一次，也绝不误伤登录用户。
   */
  if (!app.session) return false;

  return !app.session.user;
}

/*
 * 后台开关（acme-wechat.guest_readonly，默认开启）。
 * 出问题可一键关停，不用改代码重新编译。
 */
export function guestReadonlyEnabled() {
  try {
    const cfg = app.forum && app.forum.attribute ? app.forum.attribute('acmeWechat') : null;
    if (cfg && (cfg.guestReadonly === false || cfg.guestReadonly === '0')) return false;
  } catch (e) {
    // forum 还没加载完时按「开启」处理
  }

  return true;
}

/*
 * 判断一个地址是不是「帖子详情页」。
 * Flarum 的帖子页路由是 /d/<id>[-<slug>][/页码]，例如：
 *   /d/12
 *   /d/12-hello-world
 *   /d/12-hello-world/3
 */
export function isDiscussionPath(path) {
  if (!path) return false;

  try {
    const p = String(path).split('?')[0].split('#')[0];
    return /(^|\/)d\/[0-9]+([-/]|$)/.test(p);
  } catch (e) {
    return false;
  }
}

/*
 * 是否被服务端重定向过来（地址栏带 ?guestNotice=1）。
 *
 * 场景：游客手输 / 从收藏夹打开 https://w7.rs-ecard.cn/d/123，
 * 浏览器直接向服务端要这个页面，前端没有点击可以拦。
 * 服务端 RedirectGuestFromDiscussion 会 302 回 /?guestNotice=1，
 * 前端靠这个标记补上弹窗。
 *
 * 读到后会立刻把参数从地址栏清掉（replaceState，不产生历史记录），
 * 所以刷新页面不会重复弹。
 */
export function consumeGuestNoticeFlag() {
  try {
    const url = new URL(window.location.href);

    if (url.searchParams.get('guestNotice') !== '1') return false;

    url.searchParams.delete('guestNotice');
    window.history.replaceState({}, '', url.toString());

    return true;
  } catch (e) {
    return false;
  }
}

/*
 * 后端下发的「我到底能不能看正文」。
 *
 * 后端 Access\ClassAccess::canReadAnything() 算好写进 payload，
 * 前端不再自己猜班级归属（也猜不了——映射不该下发到前端）。
 * 返回 null = 还没拿到（forum payload 未就绪），此时一律按「不拦」处理，
 * 绝不能因为读不到就把正常用户锁在外面。
 */
export function canReadContent() {
  try {
    const cfg = app.forum && app.forum.attribute ? app.forum.attribute('acmeWechat') : null;

    if (cfg && typeof cfg.canReadContent === 'boolean') return cfg.canReadContent;
  } catch (e) {
    // forum 还没加载完
  }

  return null;
}

/*
 * 是否处于「内容锁定」状态。
 *
 * 1.8.5：不只游客。「已登录但没绑定班级」的普通注册用户同样看不了正文
 * （后端 ClassAccess 1.8.5 起对未绑定者一律拒绝），前端在这里一起拦掉，
 * 点帖子时立刻给提示，不必等 XHR 被后端 403 之后再由延时兜底。
 *
 * 所有拦截逻辑（点击拦截、帖子页锁定、body 标记）都只认这一个函数，
 * 保证三处判断永远一致。
 */
export function isLocked() {
  if (!guestReadonlyEnabled()) return false;

  if (isGuest()) return true;

  // 已登录：只有后端明确说「不能看」时才拦（null = 未知，不拦）
  return canReadContent() === false;
}

/*
 * 服务端判定的「这篇帖子你不能看」标记。
 *
 * 后台（Access\ClassAccess）才知道每个班级的正文权限，前端判断不了，
 * 所以服务器在渲染帖子页时给 <body> 加上
 *     AcmeContentLocked-{帖子id}
 * 并抹掉页面里的正文数据（见 HideRestrictedContent）。
 *
 * 带上帖子 id 是为了精准：SPA 从 /d/12 跳到 /d/13 时不会误锁。
 * 同时兼容 payload 里的 acmeWechat.contentLocked（另一种下发方式）。
 */
export function contentLocked() {
  try {
    const path = m.route.get() || (window.location ? window.location.pathname : '');
    const matched = String(path).match(/(?:^|\/)d\/(\d+)/);

    if (matched && document.body.classList.contains('AcmeContentLocked-' + matched[1])) {
      return true;
    }
  } catch (e) {
    // ignore
  }

  try {
    const cfg = app.forum && app.forum.attribute ? app.forum.attribute('acmeWechat') : null;

    if (cfg && cfg.contentLocked) return true;
  } catch (e) {
    // ignore
  }

  return false;
}

/*
 * 当前页面是否要显示「内容已锁定」：
 *   游客（前端可判断）或 服务端判定无权（已登录但没绑定该班级）。
 */
export function isPageLocked() {
  return isLocked() || contentLocked();
}

/*
 * 同步 body.AcmeGuest 标记。
 *
 * 【踩坑】登录/登出之后必须重新同步：游客先进站（body 加上 class），
 * 随后在站内完成微信自动登录 / 短信登录，如果标记不摘掉，
 * 已登录用户的正文会被 .AcmeGuest .Post-body{display:none} 继续隐藏
 * —— 表现就是「注册用户和游客一样，只看得到标题、看不到内容」。
 */
export function syncGuestBodyClass() {
  try {
    document.body.classList[isLocked() ? 'add' : 'remove']('AcmeGuest');
  } catch (e) {
    // ignore
  }
}

export function currentUserId() {
  try {
    return (app.session && app.session.user && app.session.user.id()) || 0;
  } catch (e) {
    return 0;
  }
}

/*
 * 同一条帖子地址只弹一次提示（刷新会重新弹）。
 * 用 sessionStorage 记录上次弹过的路径。
 */
export function maybeShowGuestNotice() {
  let path = '';

  try {
    path = m.route.get() || window.location.pathname || '';
  } catch (e) {
    path = window.location ? window.location.pathname : '';
  }

  try {
    if (sessionStorage.getItem('acmeGuestNoticePath') === path) return;
    sessionStorage.setItem('acmeGuestNoticePath', path);
  } catch (e) {
    // ignore
  }

  setTimeout(() => showGuestNotice(), 250);
}

/*
 * 诊断信息（地址栏加 ?guest=diag 时显示在页面顶部）。
 * 用来快速判断「到底是不是被当成游客了」。
 */
export function diagInfo() {
  let path = '';

  try {
    path = m.route.get();
  } catch (e) {
    path = '';
  }

  return {
    locked: isLocked(),
    isGuest: isGuest(),
    userId: currentUserId(),
    canReadContent: canReadContent(),
    enabled: guestReadonlyEnabled(),
    bodyClass: document.body && document.body.classList ? document.body.classList.contains('AcmeGuest') : false,
    path: path || (window.location ? window.location.pathname : ''),
  };
}

export function diagEnabled() {
  try {
    return new URL(window.location.href).searchParams.get('guest') === 'diag';
  } catch (e) {
    return false;
  }
}

/*
 * 弹出提示（Modal 挂不上时降级为 alert）。
 *
 * 顺带把手机端的整屏着陆页标记为「已看过」：
 * 用户都已经在点帖子了，说明早就看过 banner，之后万一回到首页
 * （例如老版本缓存还会 302），也不会再被整屏 banner 糊一次。
 */
/*
 * 是否窄屏（手机）。
 *
 * 手机上核心 Modal 会被拉到接近全屏，改样式压不住，
 * 所以窄屏改用自建的轻量浮层 components/GuestNoticeLayer.js。
 */
export function isNarrowScreen() {
  try {
    if (window.innerWidth > 0 && window.innerWidth <= 768) return true;

    return /Android|iPhone|iPad|iPod|Windows\sPhone|Mobile/i.test(navigator.userAgent || '');
  } catch (e) {
    return false;
  }
}

export function showGuestNotice() {
  markLandingSeen();

  // 手机：自建轻量浮层（宽度锁死，不会占满屏幕）
  if (isNarrowScreen()) {
    try {
      showGuestNoticeLayer(currentNotice());
      return;
    } catch (e) {
      // 浮层挂不上就退回 Modal
    }
  }

  try {
    if (app.modal && app.modal.show) {
      app.modal.show(GuestNoticeModal);
      return;
    }
  } catch (e) {
    // ignore
  }

  try {
    window.alert(currentNotice());
  } catch (e) {
    // ignore
  }
}
